import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { OrderLineItemProductName } from "@/types";

import { mapOpenSearchSourceToProductName } from "../mappers/map-opensearch-source-to-product-name";
import { ProductSearchService } from "../services/product-search.service";
import { buildProductIdsSearchBody } from "../utils/build-order-product-search-body";
import { getOpenSearchStoreStatusMessage } from "../utils/get-opensearch-store-status-message";
import { mapProductSearchHits } from "../utils/map-product-search-hits";
import { OPENSEARCH_PRODUCTS_STALE_MS } from "../utils/product-search-constants";

import { useOpenSearchStoreReadiness } from "./useOpenSearchStoreReadiness";

type OrderLineItemProductNamesStatus =
    | "loading"
    | "ready"
    | "missing_store_id"
    | "missing_pos_ids"
    | "unavailable";

type UseOrderLineItemProductNamesOptions = {
    providerStoreID?: string;
    posIds: string[];
    enabled?: boolean;
};

type UseOrderLineItemProductNamesResult = {
    namesProductsPOS: OrderLineItemProductName[];
    isLoading: boolean;
    status: OrderLineItemProductNamesStatus;
    statusMessage: ReturnType<typeof getOpenSearchStoreStatusMessage>;
};

function useOrderLineItemProductNames({
    providerStoreID,
    posIds,
    enabled = true,
}: UseOrderLineItemProductNamesOptions): UseOrderLineItemProductNamesResult {
    const storeId = providerStoreID?.trim() ?? "";
    const {
        isLoading: configsLoading,
        readiness,
        isReady,
    } = useOpenSearchStoreReadiness(storeId);

    const normalizedPosIds = useMemo(
        () => posIds.map(id => id.trim()).filter(Boolean),
        [posIds],
    );

    const status = useMemo((): OrderLineItemProductNamesStatus => {
        if (configsLoading) {
            return "loading";
        }

        if (!storeId) {
            return "missing_store_id";
        }

        if (normalizedPosIds.length === 0) {
            return "missing_pos_ids";
        }

        if (!isReady) {
            return "unavailable";
        }

        return "ready";
    }, [configsLoading, storeId, normalizedPosIds.length, isReady]);

    const statusMessage = useMemo(() => {
        if (status === "unavailable") {
            return getOpenSearchStoreStatusMessage(readiness);
        }

        return null;
    }, [status, readiness]);

    const openSearchStore =
        readiness?.status === "ready" ? readiness.store : null;

    const searchService = useMemo(() => {
        if (!openSearchStore) {
            return null;
        }

        return new ProductSearchService(
            openSearchStore.mso_store_entity_id,
            openSearchStore.mso_org_id,
        );
    }, [openSearchStore]);

    const searchBody = useMemo(
        () => buildProductIdsSearchBody(normalizedPosIds),
        [normalizedPosIds],
    );

    const canSearch = enabled && status === "ready" && Boolean(searchService);

    const { data, isFetching } = useQuery({
        queryKey: [
            "order-line-item-product-names",
            openSearchStore?.short_name ?? "",
            openSearchStore?.mso_store_entity_id ?? "",
            normalizedPosIds,
        ],
        queryFn: async () => {
            if (!searchService) {
                return [];
            }

            const response = await searchService.search({ searchBody });
            return mapProductSearchHits(
                response.hits?.hits,
                mapOpenSearchSourceToProductName,
            );
        },
        enabled: canSearch,
        placeholderData: keepPreviousData,
        staleTime: OPENSEARCH_PRODUCTS_STALE_MS,
    });

    return {
        namesProductsPOS: canSearch ? data ?? [] : [],
        isLoading: status === "loading" || (status === "ready" && isFetching),
        status,
        statusMessage,
    };
}

export { useOrderLineItemProductNames };
export type {
    OrderLineItemProductNamesStatus,
    UseOrderLineItemProductNamesResult,
};
